import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Cpu, Layers, Factory, Boxes, Printer, Sparkles,
  ShieldCheck, Zap, Globe2, GaugeCircle, FileUp, Network,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FadeUp, SectionHeading, CTASection } from "../components/Shared";
import { IMAGES } from "../lib/brand";

const STATS = [
  { value: "500+", label: "Vetted Manufacturing Partners" },
  { value: "24h", label: "Average Quote Turnaround" },
  { value: "99.2%", label: "On-Time Delivery Rate" },
  { value: "40+", label: "Materials & Finishes" },
];

const CAPABILITIES = [
  { icon: Layers, title: "Sheet Metal", desc: "Laser cutting, bending and punching with tight tolerances.", img: IMAGES.sheetMetal, span: "md:col-span-5" },
  { icon: Cpu, title: "CNC Machining", desc: "3, 4 and 5-axis milling and turning for complex geometries.", img: IMAGES.cnc, span: "md:col-span-7" },
  { icon: Factory, title: "Fabrication", desc: "Welding, assembly and structural fabrication at scale.", img: IMAGES.roboticsFactory, span: "md:col-span-7" },
  { icon: Boxes, title: "Injection Molding", desc: "Prototype to production tooling for polymer parts.", img: IMAGES.injectionMolding, span: "md:col-span-5" },
  { icon: Printer, title: "3D Printing", desc: "FDM, SLA, SLS and metal additive manufacturing.", img: IMAGES.printing3d, span: "md:col-span-5" },
  { icon: Sparkles, title: "Surface Finishing", desc: "Anodizing, powder coating, plating and polishing.", img: IMAGES.surfaceFinishing, span: "md:col-span-7" },
];

const WORKFLOW = [
  { icon: FileUp, title: "Upload & Quote", desc: "Upload CAD files and receive AI-assisted quotes with DFM feedback within hours." },
  { icon: Network, title: "Smart Matching", desc: "Orders are routed to the best-fit partner in our network based on capability, capacity and quality history." },
  { icon: GaugeCircle, title: "Live Tracking", desc: "Track production milestones, quality checkpoints and logistics in one dashboard." },
];

const WHY = [
  { icon: Zap, title: "Speed Without Compromise", desc: "Instant quoting and parallelized production slash lead times from weeks to days." },
  { icon: ShieldCheck, title: "Engineering-Grade Quality", desc: "Every partner is audited. Every order passes documented quality inspection." },
  { icon: Globe2, title: "One Platform, Global Reach", desc: "A single point of contact managing a distributed manufacturing network." },
  { icon: Cpu, title: "DFM Intelligence", desc: "Our engineering hub reviews manufacturability before a single chip is cut." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <img src={IMAGES.roboticsFactory} alt="Advanced manufacturing facility" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/70 to-slate-950/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400 mb-6 font-mono-tech">
              On-Demand Manufacturing Platform
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white leading-[1.05]">
              From CAD file to finished part.
              <span className="block font-medium text-blue-400 mt-2">Engineered. Quoted. Delivered.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
              VECTRON GLOBAL connects your engineering designs to a vetted network of precision
              manufacturers — with instant quoting, DFM review and end-to-end production management.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button asChild size="lg" className="rounded-full px-8 h-13 text-base h-12" data-testid="hero-quote-btn">
                <Link to="/contact">Start Your Project <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full px-8 h-12 text-base border-white/25 bg-transparent text-white hover:bg-white hover:text-slate-950" data-testid="hero-platform-btn">
                <Link to="/platform">Explore the Platform</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust / Stats */}
      <section className="py-20 border-b border-border bg-card" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <FadeUp key={s.label} delay={i * 0.08}>
              <p className="font-mono-tech text-3xl sm:text-4xl font-bold text-primary">{s.value}</p>
              <p className="mt-2 text-sm text-muted-foreground">{s.label}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Capabilities Bento */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <SectionHeading
            overline="Capabilities"
            title="Six manufacturing processes. One digital front door."
            description="From rapid prototypes to production runs, route every process through a single platform."
          />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {CAPABILITIES.map((c, i) => (
              <FadeUp key={c.title} delay={i * 0.05} className={c.span}>
                <Link to="/manufacturing" data-testid={`capability-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group relative block h-72 rounded-xl overflow-hidden border border-border">
                  <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <c.icon className="h-6 w-6 text-blue-400 mb-3" strokeWidth={1.5} />
                    <h3 className="text-xl font-semibold text-white font-heading">{c.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-300 max-w-sm">{c.desc}</p>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Workflow */}
      <section className="py-24 sm:py-32 bg-muted/60 border-y border-border" data-testid="digital-workflow-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionHeading
              overline="Digital Workflow"
              title="Your entire supply chain, one dashboard."
              description="No more email chains and spreadsheet chaos. Vectron digitizes quoting, ordering, production tracking and quality documentation."
            />
            <div className="space-y-8 -mt-6">
              {WORKFLOW.map((w, i) => (
                <FadeUp key={w.title} delay={i * 0.1}>
                  <div className="flex gap-5">
                    <div className="h-12 w-12 shrink-0 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <w.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold font-heading">{w.title}</h4>
                      <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
          <FadeUp delay={0.15}>
            <div className="relative rounded-xl overflow-hidden border border-border">
              <img src={IMAGES.abstractData} alt="Digital manufacturing platform" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-primary/10" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Why Vectron */}
      <section className="py-24 sm:py-32" data-testid="why-vectron-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <SectionHeading
            overline="Why Vectron"
            title="Built by engineers, for engineers."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => (
              <FadeUp key={w.title} delay={i * 0.08}>
                <div className="h-full p-7 rounded-xl bg-card border border-border transition-transform duration-200 hover:-translate-y-1" data-testid={`why-card-${i}`}>
                  <w.icon className="h-6 w-6 text-accent mb-5" strokeWidth={1.5} />
                  <h4 className="text-lg font-semibold font-heading">{w.title}</h4>
                  <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
